import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { track, EVENTS } from '../lib/analytics';
import { userData, MODE } from '../lib/dataClient';
import { useAuth } from './AuthContext';

const GiwahsContext = createContext(null);

const ANON_SAVED_KEY   = 'giwahs_saved_suppliers';
const ANON_COMPARE_KEY = 'giwahs_compare_suppliers';
const ANON_RECENT_KEY  = 'giwahs_recently_viewed_suppliers';
const userRecentKey    = (uid) => `giwahs_recently_viewed_suppliers_user_${uid}`;
const RECENT_CAP       = 10;

const readJSON  = (key, fb) => { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fb; } catch { return fb; } };
const writeJSON = (key, v)  => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };

export const GiwahsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const uid = user?.id || null;

  const [savedSuppliers, setSavedSuppliers] = useState([]);
  const [compareList,    setCompareList]    = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [hydrating,      setHydrating]      = useState(true);
  const prevUidRef = useRef(undefined);

  // ─── Load / merge whenever auth state stabilises ───────────────────
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    const prev = prevUidRef.current;
    prevUidRef.current = uid;

    (async () => {
      setHydrating(true);
      try {
        if (uid) {
          // Authenticated: merge anon lists on first sign-in transition,
          // then read user-scoped lists via dataClient (mock or supabase).
          // Note: the (prev === null || prev === undefined) gate also
          // matches a full page reload while signed in — that's intentional
          // and safe; mergeAnonymousIntoUser is a no-op when anon keys are
          // already empty (which is the steady state after the first run).
          if (prev === null || prev === undefined) {
            const anonSaved   = readJSON(ANON_SAVED_KEY,   []);
            const anonCompare = readJSON(ANON_COMPARE_KEY, []);
            if (anonSaved.length || anonCompare.length) {
              const merged = await userData.mergeAnonymousIntoUser(uid);
              if (merged.saved + merged.compared > 0) {
                toast.success(`Synced ${merged.saved} saved + ${merged.compared} compare item(s) from your last session.`);
              }
            }
          }
          const [saved, compare] = await Promise.all([
            userData.listSaved(uid),
            userData.listCompare(uid),
          ]);
          if (cancelled) return;
          setSavedSuppliers(saved);
          setCompareList(compare);

          // Recently-viewed: load user-scoped list. On first sign-in
          // transition, also fold in the anon list (dedup, most-recent-wins).
          let recent = readJSON(userRecentKey(uid), []);
          if (prev === null || prev === undefined) {
            const anonRecent = readJSON(ANON_RECENT_KEY, []);
            if (anonRecent.length) {
              // Anonymous wins for the head of the list (more recent).
              const merged = Array.from(new Set([...anonRecent, ...recent])).slice(0, RECENT_CAP);
              writeJSON(userRecentKey(uid), merged);
              recent = merged;
              try { localStorage.removeItem(ANON_RECENT_KEY); } catch {}
            }
          }
          setRecentlyViewed(recent);
        } else {
          // Anonymous: localStorage anon keys, unchanged behaviour.
          if (cancelled) return;
          setSavedSuppliers(readJSON(ANON_SAVED_KEY,   []));
          setCompareList(   readJSON(ANON_COMPARE_KEY, []));
          setRecentlyViewed(readJSON(ANON_RECENT_KEY,  []));
        }
      } catch (err) {
        console.error('[GIWAHS] failed to hydrate saved/compare', err);
        if (!cancelled) {
          // Fallback: keep current state, don't blow up the UI.
          toast.error('Could not load your saved suppliers. Showing local copy.');
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [uid, authLoading]);

  // ─── Toggle saved (optimistic for both anon + authed paths) ────────
  const toggleSaved = useCallback(async (supplierId) => {
    const prev = savedSuppliers;
    const wasSaved = prev.includes(supplierId);
    const next = wasSaved ? prev.filter(id => id !== supplierId) : [...prev, supplierId];

    // Optimistic apply.
    setSavedSuppliers(next);
    track(wasSaved ? EVENTS.SUPPLIER_UNSAVED : EVENTS.SUPPLIER_SAVED, {
      supplier_id: supplierId, saved_total: next.length, authed: !!uid,
    });

    try {
      if (uid) {
        if (wasSaved) await userData.removeSaved(uid, supplierId);
        else          await userData.addSaved(uid,    supplierId);
      } else {
        writeJSON(ANON_SAVED_KEY, next);
      }
    } catch (err) {
      console.error('[GIWAHS] toggleSaved failed', err);
      setSavedSuppliers(prev); // rollback
      toast.error(err?.message || 'Could not update your saved list. Please try again.');
    }
  }, [savedSuppliers, uid]);

  // ─── Toggle compare (optimistic, cap of 4) ─────────────────────────
  const toggleCompare = useCallback(async (supplierId) => {
    const prev = compareList;
    let next; let event = null;
    if (prev.includes(supplierId)) {
      next = prev.filter(id => id !== supplierId);
      event = EVENTS.SUPPLIER_UNCOMPARED;
    } else if (prev.length >= 4) {
      toast.error('Compare list is full (max 4 suppliers).');
      return;
    } else {
      next = [...prev, supplierId];
      event = EVENTS.SUPPLIER_COMPARED;
    }

    setCompareList(next);
    track(event, { supplier_id: supplierId, compare_total: next.length, authed: !!uid });

    try {
      if (uid) {
        if (event === EVENTS.SUPPLIER_UNCOMPARED) await userData.removeCompare(uid, supplierId);
        else                                       await userData.addCompare(uid,    supplierId);
      } else {
        writeJSON(ANON_COMPARE_KEY, next);
      }
    } catch (err) {
      console.error('[GIWAHS] toggleCompare failed', err);
      setCompareList(prev);
      toast.error(err?.message || 'Could not update your compare list. Please try again.');
    }
  }, [compareList, uid]);

  const clearCompare = useCallback(async () => {
    const prev = compareList;
    setCompareList([]);
    try {
      if (uid) await userData.clearCompare(uid);
      else     writeJSON(ANON_COMPARE_KEY, []);
    } catch (err) {
      console.error('[GIWAHS] clearCompare failed', err);
      setCompareList(prev);
      toast.error('Could not clear your compare list. Please try again.');
    }
  }, [compareList, uid]);

  // ─── Recently viewed (client-side only, cap 10) ────────────────────
  // Source-of-truth is localStorage (not React state) — this avoids a
  // race during full-page navigations where the async hydration effect
  // can overwrite a fresh recordView before its state lands.
  const recordView = useCallback((supplierLegacyId) => {
    if (!supplierLegacyId) return;
    const key = uid ? userRecentKey(uid) : ANON_RECENT_KEY;
    const current = readJSON(key, []);
    const next = [supplierLegacyId, ...current.filter(id => id !== supplierLegacyId)].slice(0, RECENT_CAP);
    writeJSON(key, next);
    setRecentlyViewed(next);
  }, [uid]);

  // Legacy synchronous helpers retained for compatibility — write through
  // the same path. (Callers in current codebase: none use these as of now.)
  const persistSaved   = useCallback((next) => {
    setSavedSuppliers(next);
    if (uid && MODE === 'mock') writeJSON(`giwahs_saved_suppliers_user_${uid}`,   next);
    else if (!uid)              writeJSON(ANON_SAVED_KEY,    next);
  }, [uid]);
  const persistCompare = useCallback((next) => {
    setCompareList(next);
    if (uid && MODE === 'mock') writeJSON(`giwahs_compare_suppliers_user_${uid}`, next);
    else if (!uid)              writeJSON(ANON_COMPARE_KEY,  next);
  }, [uid]);

  return (
    <GiwahsContext.Provider value={{
      savedSuppliers, compareList, recentlyViewed,
      hydrating, persistenceMode: MODE,
      toggleSaved, toggleCompare, clearCompare, recordView,
      persistSaved, persistCompare,
    }}>
      {children}
    </GiwahsContext.Provider>
  );
};

export const useGiwahs = () => {
  const ctx = useContext(GiwahsContext);
  if (!ctx) throw new Error('useGiwahs must be used inside GiwahsProvider');
  return ctx;
};
